import { useEffect, useMemo, useRef, useState } from "react";
import {
  App as AntdApp,
  Button,
  Card,
  Col,
  Divider,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Tag,
  Typography,
  Tooltip,
  Spin,
  Popconfirm
} from "antd";
import { StarOutlined, StarFilled, PlusOutlined, CheckOutlined } from "@ant-design/icons";
// оставим как есть, сервисы переведём позже
import { useAuth } from "../auth/AuthContext.tsx";

import { getPlatforms } from "../api/platforms.ts";
import { getExchanges } from "../api/exchanges.ts";
import faviconUrl from "../logo.png";

// Пагинация + пины
import {
  getMyOrdersPaged,
  getMyPinnedOrders,
  deleteOrder,
  updateOrder,
  searchMyOrdersPaged,
  patchOrderComment,
} from "../api/orders.ts";

// Инвойсы теперь внутри ордеров
import { deleteInvoice } from "../api/invoices.ts";
import {
  saveOrder,
  addInvoice,
  closeOrder,
  pin as pinOrder,
  unpin as unpinOrder,
  addTopup,
  addRefund,
} from "../api/workflow.ts";

import {
  calcEffectiveRate,
  calcOrderAggregates,
  getMarkup,
  getMode,
  percentToBb,
  rateFromUsdt,
  usdtFromRate,
  // Если у тебя нет .d.ts для finance, локально объявим:
  // type InputMode = "RateOnly" | "AmountUsdtOnly" | "Both";
} from "../lib/finance.ts";

import "./worker-orders.css";

const { Title } = Typography;

/* =========================================================
   Типы (минимально достаточные под текущую страницу)
========================================================= */

type Id = number;

type InputMode = "RateOnly" | "AmountUsdtOnly" | "Both";

export interface PlatformVM {
  id: Id;
  name?: string;
  // возможные поля, которые читает getMode/getMarkup
  markupPercent?: number | null;
  inputMode?: InputMode | string;
  [k: string]: unknown;
}

export interface InvoiceVM {
  id: Id;
  orderId: Id;
  platformId: Id;
  invoiceExternalId: string;
  amountFiat: number;
  amountUsdt: number | null;
  platformRate: number | null;
  effectiveRate: number;
  percentToBb: number;
  paidAt?: string | null;
  note?: string | null;
  // служебные флаги UI
  _deleted?: boolean;
  _new?: boolean;
}

export interface OrderVM {
  id: Id;
  exchangeOrderExternalId: string;
  orderAmountFiat: number;
  exchangeRate: number;
  executedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  isDraft: boolean;
  statusId?: number;
  statusName?: string;
  status?: number;
  cardTopUp: number;
  refund: number;
  comment?: string | null;
  tag?: string | null;
}

type NewInvoiceDraft = {
  amountFiat: number | null;
  platformId: Id | null;
  invoiceId: string;
  usdt: number | null;
  platformRate: number | null;
};

type InvoicePatchAdd = {
  _op: typeof OP.ADD;
  platformId: Id;
  invoiceExternalId: string;
  amountFiat: number;
  amountUsdt: number | null;
  platformRate: number | null;
};

type InvoicePatchUpdate = {
  _op: typeof OP.UPDATE;
  id: Id;
  platformId?: Id;
  invoiceExternalId?: string;
  amountFiat?: number;
  amountUsdt?: number | null;
  platformRate?: number | null;
};

type InvoicePatchDelete = {
  _op: typeof OP.DELETE;
  id: Id;
};

type InvoicePatch = InvoicePatchAdd | InvoicePatchUpdate | InvoicePatchDelete;

type OrderPatch = Partial<{
  exchangeOrderExternalId: string;
  orderAmountFiat: number;
  exchangeRate: number;
  cardTopUp: number;
  refund: number;
  tag: string | null; 
}>;

/* ---------- Утилиты UI ---------- */
const money = (v: unknown): string => {
  const n = Number.isFinite(+((v as number) ?? 0)) ? +(v as number) : 0;
  const clamp = Math.abs(n) < 0.005 ? 0 : n;
  return clamp.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const AGG_OPTS = { average: "simple", gateByCoverage: false } as const;
const pct = (v: number | null | undefined): string => `${(((v ?? 0) as number) * 100).toFixed(2)}%`;

// нормализуем id в число ОДИН РАЗ
const idN = (x: unknown): number => Number(x) || 0;

// Опкоды для бэка (InvoicePatchOp): Add=0, Update=1, Delete=2
const OP = {
  ADD: 0,
  UPDATE: 1,
  DELETE: 2,
} as const;

/* ---------- Нормализация ввода чисел (запятая/точка/разделители) ---------- */
const normalizeNumberInput = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  let s = String(value);
  s = s.replace(/[\s\u00A0]/g, "").replace(/[^\d.,-]/g, "");
  if (s.includes("-")) s = (s[0] === "-" ? "-" : "") + s.replace(/-/g, "").replace(/^-/, "");
  const lastComma = s.lastIndexOf(",");
  const lastDot = s.lastIndexOf(".");
  const hasSep = lastComma !== -1 || lastDot !== -1;
  if (hasSep) {
    const sepIndex = Math.max(lastComma, lastDot);
    const intPart = s.slice(0, sepIndex).replace(/[.,]/g, "");
    const fracPart = s.slice(sepIndex + 1).replace(/[.,]/g, "");
    s = `${intPart}.${fracPart}`;
  } else {
    s = s.replace(/[.,]/g, "");
  }
  return s;
};

const numParser = (displayValue?: string): number => {
  const s = normalizeNumberInput(displayValue);
  if (!s || s === "-" || s === ".") return NaN; // пустое/промежуточное состояние
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

/* ---------- Мапперы ---------- */
function shallowEqual<T extends Record<string, any>>(a: T, b: T, fields: (keyof T & string)[]) {
  return fields.every((f) => (a?.[f] ?? null) === (b?.[f] ?? null));
}

function toInvoice(i: any): InvoiceVM {
  return {
    id: idN(i.id ?? i.Id),
    orderId: idN(i.orderId ?? i.OrderId),
    platformId: idN(i.platformId ?? i.PlatformId),
    invoiceExternalId: i.invoiceExternalId ?? i.InvoiceExternalId ?? "",
    amountFiat: i.amountFiat ?? i.AmountFiat ?? 0,
    amountUsdt: i.amountUsdt ?? i.AmountUsdt ?? null,
    platformRate: i.platformRate ?? i.PlatformRate ?? null,
    effectiveRate: i.effectiveRate ?? i.EffectiveRate ?? 0,
    percentToBb: i.percentToBb ?? i.PercentToBb ?? 0,
    paidAt: i.paidAt ?? i.PaidAt ?? null,
    note: i.note ?? i.Note ?? null,
  };
}

function buildInvMapFromApiOrders(apiOrders: any[]): Record<number, InvoiceVM[]> {
  const map: Record<number, InvoiceVM[]> = {};
  for (const o of apiOrders || []) {
    const id = o.id ?? o.Id;
    const invs = o.invoices ?? o.Invoices;
    if (id == null || !Array.isArray(invs)) continue;
    map[idN(id)] = invs.map(toInvoice);
  }
  return map;
}

function toVM(o: any): OrderVM {
  return {
    id: idN(o.id ?? o.Id),
    exchangeOrderExternalId:
      o.exchangeOrderExternalId ??
      o.ExchangeOrderExternalId ??
      o.exchangeOrderId ??
      o.exchangeOrderExternalID ??
      "",
    orderAmountFiat: o.orderAmountFiat ?? o.OrderAmountFiat ?? o.orderAmount ?? 0,
    exchangeRate: o.exchangeRate ?? o.ExchangeRate ?? o.bbRate ?? 0,
    executedAt:
      o.executedAt ?? o.ExecutedAt ?? o.updatedAt ?? o.UpdatedAt ?? o.createdAt ?? o.CreatedAt ?? null,
    createdAt: o.createdAt ?? o.CreatedAt ?? null,
    updatedAt: o.updatedAt ?? o.UpdatedAt ?? null,
    isDraft:
      (o.statusId ?? o.Status) === 1 ||
      (typeof (o.statusName ?? o.StatusName) === "string" &&
        (o.statusName ?? o.StatusName).toLowerCase() === "draft"),
    statusId: o.statusId ?? o.Status ?? 3,
    statusName: o.statusName ?? o.StatusName,
    cardTopUp: o.cardTopUp ?? o.CardTopUp ?? 0,
    refund: o.refund ?? o.Refund ?? 0,
    comment: o.comment ?? o.Comment ?? null,
    tag: o.tag ?? o.Tag ?? null,
  };
}

// сортировка по дате исполнения (desc)
const sortByExecutedDesc = (a: Pick<OrderVM, "executedAt">, b: Pick<OrderVM, "executedAt">) =>
  new Date(b.executedAt as any).getTime() - new Date(a.executedAt as any).getTime();

/* ---------- формат даты ---------- */
const fmtDateTime = (v: unknown): string => {
  if (!v) return "—";
  const d = new Date(v as any);
  if (isNaN(d.getTime())) return "—";
  const date = d.toLocaleDateString("ru-RU"); // 12.09.2025
  const time = d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit", hour12: false }); // 14:37
  return `${date} ${time}`;
};

/* ---------------- карточка ---------------- */

interface OrderCardProps {
  vm: OrderVM;
  platforms: PlatformVM[];
  invoices: InvoiceVM[];
  onAddInvoice: (vm: OrderVM, draft: NewInvoiceDraft, platform: PlatformVM) => Promise<void>;
  onDeleteInvoice: (orderId: Id, invoiceId: Id) => Promise<void>;
  onDeleteOrder: (orderId: Id) => Promise<void>;
  onCloseDraft: (vm: OrderVM, invs: InvoiceVM[], displayProfit: number, comment: string | null) => Promise<void>;
// OrderCardProps
onApplyAllChanges: (
  vm: OrderVM,
  orderPatch: OrderPatch,
  invoicePatches: InvoicePatch[],
  platforms: PlatformVM[],
  comment: string | null,
  tag: string | null, 
) => Promise<boolean>;

  isPinned: boolean;
  onTogglePin: (id: Id, pinned: boolean) => Promise<void>;
  onAddTopup: (vm: OrderVM, amount: number) => Promise<boolean>;
  onAddRefund: (vm: OrderVM, amount: number) => Promise<boolean>;
}

function OrderCard({
  vm,
  platforms,
  invoices,
  onAddInvoice,
  onDeleteInvoice,
  onDeleteOrder,
  onCloseDraft,
  onApplyAllChanges,
  isPinned,
  onTogglePin,
  onAddTopup,
  onAddRefund,
}: OrderCardProps) {
  const { message } = AntdApp.useApp();
  const [editing, setEditing] = useState(false);

  const [orderEdit, setOrderEdit] = useState<Required<OrderPatch> & { comment: string }>({
    exchangeOrderExternalId: vm.exchangeOrderExternalId || "",
    orderAmountFiat: vm.orderAmountFiat || 0,
    exchangeRate: vm.exchangeRate || 0,
    cardTopUp: vm.cardTopUp ?? 0,
    refund: vm.refund ?? 0,
    comment: (vm.comment ?? "") as string,
    tag: (vm.tag ?? "") as string,
  });

  const [invEdits, setInvEdits] = useState<InvoiceVM[]>([]);

  // локальные состояния для инлайн-добива/возврата
  const [showTopup, setShowTopup] = useState(false);
  const [showRefund, setShowRefund] = useState(false);
  const [topupAmount, setTopupAmount] = useState<number | null>(null);
  const [refundAmount, setRefundAmount] = useState<number | null>(null);
  const [busyTR, setBusyTR] = useState(false);

  useEffect(() => {
  setOrderEdit({
    exchangeOrderExternalId: vm.exchangeOrderExternalId || "",
    orderAmountFiat: vm.orderAmountFiat || 0,
    exchangeRate: vm.exchangeRate || 0,
    cardTopUp: vm.cardTopUp ?? 0,
    refund: vm.refund ?? 0,
    comment: vm.comment ?? "",
    tag: vm.tag ?? "",             // ← один раз, при смене ордера
  });
  setInvEdits(invoices.map(x => ({ ...x })));
  setEditing(false);
  setShowTopup(false);
  setShowRefund(false);
  setTopupAmount(null);
  setRefundAmount(null);
}, [vm.id]); // ← важно
useEffect(() => {
  setOrderEdit(s => ({
    ...s,                                   // ← сохраняем s.comment
    exchangeOrderExternalId: vm.exchangeOrderExternalId || "",
    orderAmountFiat: vm.orderAmountFiat || 0,
    exchangeRate: vm.exchangeRate || 0,
    cardTopUp: vm.cardTopUp ?? 0,
    refund: vm.refund ?? 0,
  }));
  setInvEdits(invoices.map(x => ({ ...x })));
}, [invoices, vm.exchangeOrderExternalId, vm.orderAmountFiat, vm.exchangeRate, vm.cardTopUp, vm.refund]);
  const vmForSummary = useMemo<OrderVM>(() => {
    if (!editing) return vm;
    return {
      ...vm,
      orderAmountFiat: +orderEdit.orderAmountFiat || 0,
      exchangeRate: +orderEdit.exchangeRate || 0,
      cardTopUp: +orderEdit.cardTopUp || 0,
      refund: +orderEdit.refund || 0,
    };
  }, [editing, vm, orderEdit]);

  const invsForSummary = useMemo<InvoiceVM[]>(() => {
    const src = editing ? invEdits : invoices;
    return (src || [])
      .filter((e: InvoiceVM) => !e?._deleted)
      .map((e: InvoiceVM) => {
        const plat = (platforms || []).find((p) => p.id === e.platformId) || ({} as PlatformVM);
        const eff = calcEffectiveRate({
          amountFiat: +e.amountFiat || 0,
          usdt: e.amountUsdt != null ? +e.amountUsdt : null,
          platformRate: e.platformRate != null ? +e.platformRate : null,
          markupPercent: getMarkup(plat),
          mode: getMode(plat) as InputMode,
        });
        return { ...e, effectiveRate: (eff ?? e.effectiveRate ?? e.platformRate ?? 0) as number };
      });
  }, [editing, invEdits, platforms, invoices]);

  const aggregates = useMemo(
  () => calcOrderAggregates(vmForSummary, invsForSummary, AGG_OPTS),
  [vmForSummary, invsForSummary]
);

  // для бейджа «Итог» используем aggregates.display (!!!)
const saveTimerRef = useRef<number | null>(null);

const handleCommentChange = (val: string) => {
  // локально меняем поле (UI отзывчивый)
  setOrderEdit(s => ({ ...s, comment: val }));

  // сохраняем ТОЛЬКО вне режима редактирования и когда ордер открыт
  if (editing || !isOpen) return;

  if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
  saveTimerRef.current = window.setTimeout(async () => {
    const next = (val || "").trim() || null;
    const res = await patchOrderComment(vm.id, next);
    if (!res.ok) {
      message.error("Не удалось сохранить комментарий");
      // по желанию можно откатить: setOrderEdit(s => ({ ...s, comment: vm.comment ?? "" }));
    }
  }, 600); // debounce 600 мс
};
  const isOpen =
  !vm.isDraft &&
  (vm.statusId === 2 ||
    (vm as any).status === 2 ||
    (typeof vm.statusName === "string" && vm.statusName.toLowerCase() === "open"));
const isClosed = !vm.isDraft && !isOpen;
const canEditComment = !isClosed || editing;
const canAddInvoice = isClosed ? editing : true;
// ВСЕГДА показываем «засечённый» итог
const displayProfit: number = aggregates.display;
const profitPositive = displayProfit >= 0;

  const renderInvoiceRow = (editMode: boolean, row: InvoiceVM, idx: number) => {
    const platform = (platforms.find((p) => p.id === row.platformId) || {}) as PlatformVM;
    const mode = getMode(platform) as InputMode;
    const deleted = !!row._deleted;

    if (!editMode) {
      return (
        <div className="wo-invoice-row" key={row.id}>
          <div className="wo-invoice-chip">
            <div className="wo-invoice-chip-text">
              <b>Сумма инвойса:</b> {money(row.amountFiat)} ₽{" "}
              {"  |  "}
              <b>Платформа:</b> {platform?.name || row.platformId}
              {"  |  "}
              <b>ID:</b> {row.invoiceExternalId}
              {"  |  "}
              <b>USDT:</b> {row.amountUsdt ?? "-"}
              {"  |  "}
              <b>Курс:</b> {row.effectiveRate ?? row.platformRate ?? "-"} ₽
            </div>

            {!isClosed && (
              <Button danger className="wo-delete-btn" onClick={() => onDeleteInvoice(vm.id, row.id)}>
                Удалить
              </Button>
            )}
          </div>
        </div>
      );
    }

    // --- Режим редактирования ---
    return (
      <div className="wo-invoice-row" key={row.id} style={{ opacity: deleted ? 0.6 : 1 }}>
        <Row gutter={[8, 8]} style={{ width: "100%" }} align="middle">
          <Col xs={24} md={4}>
            <InputNumber
              placeholder="Сумма (₽)"
              min={0}
              step={0.01}
              style={{ width: "100%" }}
              disabled={deleted}
              value={row.amountFiat}
              parser={numParser}
              onChange={(v) =>
                setInvEdits((a) => {
                  const c = [...a];
                  const cur = { ...c[idx], amountFiat: (v as number) ?? 0 };
                  const markup = getMarkup(platform);
                  if (mode === "AmountUsdtOnly") {
                    if (cur.amountUsdt != null)
                      cur.platformRate = rateFromUsdt(cur.amountFiat, cur.amountUsdt, markup);
                  } else if (mode === "RateOnly") {
                    if (cur.platformRate != null)
                      cur.amountUsdt = usdtFromRate(cur.amountFiat, cur.platformRate);
                  } else {
                    // Both
                    if (cur.amountUsdt != null) {
                      cur.platformRate = rateFromUsdt(cur.amountFiat, cur.amountUsdt, markup);
                    } else if (cur.platformRate != null) {
                      cur.amountUsdt = usdtFromRate(cur.amountFiat, cur.platformRate);
                    }
                  }
                  c[idx] = cur as InvoiceVM;
                  return c;
                })
              }
            />
          </Col>

          <Col xs={24} md={4}>
            <Select
              placeholder="Площадка"
              value={row.platformId}
              style={{ width: "100%" }}
              disabled={deleted}
              options={(platforms || []).map((p) => ({ value: p.id, label: p.name ?? p.id }))}
              showSearch
              optionFilterProp="label"
              onChange={(v) =>
                setInvEdits((a) => {
                  const c = [...a];
                  const nextPlat = (platforms.find((p) => p.id === (v as number)) ||
                    {}) as PlatformVM;
                  const nextMode = getMode(nextPlat) as InputMode;
                  const nextMarkup = getMarkup(nextPlat);

                  const cur = { ...c[idx], platformId: v as number } as InvoiceVM;
                  const amount = +cur.amountFiat || 0;

                  if (nextMode === "RateOnly") {
                    if (cur.platformRate != null && amount > 0) {
                      cur.amountUsdt = usdtFromRate(amount, +cur.platformRate);
                    }
                  } else if (nextMode === "AmountUsdtOnly") {
                    if (cur.amountUsdt != null && amount > 0) {
                      cur.platformRate = rateFromUsdt(amount, +cur.amountUsdt, nextMarkup);
                    } else {
                      cur.platformRate = null;
                    }
                  } else {
                    // Both
                    if (cur.amountUsdt != null && amount > 0) {
                      cur.platformRate = rateFromUsdt(amount, +cur.amountUsdt, nextMarkup);
                    } else if (cur.platformRate != null && amount > 0) {
                      cur.amountUsdt = usdtFromRate(amount, +cur.platformRate);
                    }
                  }

                  c[idx] = cur;
                  return c;
                })
              }
            />
          </Col>

          <Col xs={24} md={6}>
            <Input
              placeholder="ID инвойса"
              disabled={deleted}
              value={row.invoiceExternalId}
              onChange={(e) =>
                setInvEdits((a) => {
                  const c = [...a];
                  c[idx] = { ...c[idx], invoiceExternalId: e.target.value } as InvoiceVM;
                  return c;
                })
              }
            />
          </Col>

          <Col xs={24} md={4}>
            <InputNumber
              placeholder="USDT"
              min={0}
              step={0.0001}
              style={{ width: "100%" }}
              disabled={deleted || mode === "RateOnly"}
              value={row.amountUsdt as number | null}
              parser={numParser}
              onChange={(v) =>
                setInvEdits((a) => {
                  const c = [...a];
                  const cur = { ...c[idx], amountUsdt: (v as number | null) } as InvoiceVM;
                  if (mode === "Both" || mode === "AmountUsdtOnly") {
                    const markup = getMarkup(platform);
                    cur.platformRate = rateFromUsdt(cur.amountFiat, cur.amountUsdt, markup);
                  }
                  c[idx] = cur;
                  return c;
                })
              }
            />
          </Col>

          <Col xs={24} md={4}>
            <InputNumber
              placeholder="Курс площадки"
              min={0}
              step={0.0001}
              style={{ width: "100%" }}
              disabled={deleted || mode === "AmountUsdtOnly"}
              value={row.platformRate as number | null}
              parser={numParser}
              onChange={(v) =>
                setInvEdits((a) => {
                  const c = [...a];
                  const cur = { ...c[idx], platformRate: (v as number | null) } as InvoiceVM;
                  if (mode === "Both" || mode === "RateOnly") {
                    cur.amountUsdt = usdtFromRate(cur.amountFiat, cur.platformRate);
                  }
                  c[idx] = cur;
                  return c;
                })
              }
            />
          </Col>

          <Col xs={24} md={2}>
            <Button
            className="wo-delete-btn"
              danger={!deleted}
              onClick={() =>
                setInvEdits((a) => {
                  const c = [...a];
                  c[idx] = { ...c[idx], _deleted: !c[idx]._deleted } as InvoiceVM;
                  return c;
                })
              }
            >
              {deleted ? "Восстановить" : "Удалить"}
            </Button>
          </Col>
        </Row>
        {deleted && <div className="wo-invoice-removed-hint">Удалён</div>}
      </div>
    );
  };

  return (
  <Card className="wo-card" styles={{ body: { padding: 16 } }}>
    {/* ШАПКА */}
    <div className="wo-card-head">
      <div className="wo-title-wrap">
        {!editing && (
          <Button
            className="wo-pin-abs"
            shape="circle"
            icon={isPinned ? <StarFilled /> : <StarOutlined />}
            onClick={() => onTogglePin(vm.id, isPinned)}
          />
        )}

        <div className="wo-order-title">
          Order #{vm.id} от: {fmtDateTime(vm.createdAt)}
        </div>

        {!editing ? (
          <div className="wo-order-meta">
            <div>Сумма: {money(vm.orderAmountFiat)} ₽</div>
            <div>ID: {vm.exchangeOrderExternalId}</div>
            <div>Курс: {money(vm.exchangeRate)} ₽</div>
            <div>Добив: {money(vm.cardTopUp)} ₽</div>
            <div>Возврат: {money(vm.refund)} ₽</div>
            <div>Процент: {pct(aggregates.avgPercent)}</div>
            {vm.updatedAt && <div>Обновлён: {fmtDateTime(vm.updatedAt)}</div>}
            {vm.isDraft && <div className="wo-badge-draft">Черновик</div>}
            {vm.tag && <div className="wo-badge-draft">{vm.tag}</div>}
          </div>
        ) : (
          <div className="wo-order-meta-edit">
            <div
              className={`wo-field ${
                +orderEdit.orderAmountFiat !== +vm.orderAmountFiat ? "wo-dirty" : ""
              }`}
            >
              <div className="wo-label">
                Сумма сделки <span className="wo-unit">₽</span>
              </div>
              <InputNumber
                size="small"
                min={0}
                step={0.01}
                value={orderEdit.orderAmountFiat}
                parser={numParser}
                onChange={(v) => setOrderEdit((s) => ({ ...s, orderAmountFiat: (v as number) ?? 0 }))}
                style={{ width: "100%" }}
              />
            </div>

            <div
              className={`wo-field ${
                orderEdit.exchangeOrderExternalId !== (vm.exchangeOrderExternalId || "") ? "wo-dirty" : ""
              }`}
            >
              <div className="wo-label">ID сделки</div>
              <Input
                size="small"
                value={orderEdit.exchangeOrderExternalId}
                onChange={(e) =>
                  setOrderEdit((s) => ({ ...s, exchangeOrderExternalId: e.target.value }))
                }
              />
            </div>

            <div className={`wo-field ${+orderEdit.exchangeRate !== +vm.exchangeRate ? "wo-dirty" : ""}`}>
              <div className="wo-label">
                Курс <span className="wo-unit">₽/USDT</span>
              </div>
              <InputNumber
                size="small"
                min={0}
                step={0.0001}
                value={orderEdit.exchangeRate}
                parser={numParser}
                onChange={(v) => setOrderEdit((s) => ({ ...s, exchangeRate: (v as number) ?? 0 }))}
                style={{ width: "100%" }}
              />
            </div>

            <div className={`wo-field ${+orderEdit.cardTopUp !== +vm.cardTopUp ? "wo-dirty" : ""}`}>
              <div className="wo-label">
                Добив с карты <span className="wo-unit">₽</span>
              </div>
              <InputNumber
                size="small"
                min={0}
                step={0.01}
                value={orderEdit.cardTopUp}
                parser={numParser}
                onChange={(v) => setOrderEdit((s) => ({ ...s, cardTopUp: (v as number) ?? 0 }))}
                style={{ width: "100%" }}
              />
            </div>

            <div className={`wo-field ${+orderEdit.refund !== +vm.refund ? "wo-dirty" : ""}`}>
              <div className="wo-label">
                Возврат <span className="wo-unit">₽</span>
              </div>
              <InputNumber
                size="small"
                min={0}
                step={0.01}
                value={orderEdit.refund}
                parser={numParser}
                onChange={(v) => setOrderEdit((s) => ({ ...s, refund: (v as number) ?? 0 }))}
                style={{ width: "100%" }}
              />
            </div>
            <div className={`wo-field ${(orderEdit.tag ?? "") !== (vm.tag ?? "") ? "wo-dirty" : ""}`}>
  <div className="wo-label">Тег</div>
  <Input
    size="small"
    value={orderEdit.tag ?? ""}
    onChange={(e) => setOrderEdit((s) => ({ ...s, tag: e.target.value }))}
  />
</div>
          </div>
          
        )}

        <Tag
          bordered
          className="wo-total"
          style={{
            marginTop: 8,
            borderColor: displayProfit > 0 ? "#52c41a" : "#ff4d4f",
            color: displayProfit > 0 ? "#52c41a" : "#ff4d4f",
          }}
        >
          Итог: {money(displayProfit)} ₽
        </Tag>
      </div>
{/* ПРАВАЯ КОЛОНКА: кнопки + комментарий в одном grid-элементе */}
<div
  style={{
    gridColumn: "2 / 3",
    justifySelf: "end",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    width: "min(420px, 100%)",
  }}
>
  <Space align="center" className="wo-card-actions" style={{ alignSelf: "stretch" }}>
    {!editing && (
      <Button
        className="wo-edit-btn"
        color="primary"
        variant="filled"
        onClick={() => {
          setEditing(true);
          setInvEdits(invoices.map((x) => ({ ...x })));
        }}
      >
        Редактировать
      </Button>
    )}

    {editing && (
      <>
        <Button
          type="primary"
          onClick={async () => {
            const orderPatch: OrderPatch = {};
            if (orderEdit.exchangeOrderExternalId !== (vm.exchangeOrderExternalId || "")) {
              orderPatch.exchangeOrderExternalId = (orderEdit.exchangeOrderExternalId || "").trim();
            }
            if (+orderEdit.orderAmountFiat !== +vm.orderAmountFiat) {
              orderPatch.orderAmountFiat = +orderEdit.orderAmountFiat || 0;
            }
            if (+orderEdit.exchangeRate !== +vm.exchangeRate) {
              orderPatch.exchangeRate = +orderEdit.exchangeRate || 0;
            }
            if (+orderEdit.cardTopUp !== +vm.cardTopUp) {
              orderPatch.cardTopUp = +orderEdit.cardTopUp || 0;
            }
            if (+orderEdit.refund !== +vm.refund) {
              orderPatch.refund = +orderEdit.refund || 0;
            }
            if ((orderEdit.tag ?? "") !== (vm.tag ?? "")) {
  orderPatch.tag = (orderEdit.tag || "").trim() || null;   // ← NEW
}

            const changedInvoices: InvoicePatch[] = [];
            const afterById: Record<number, InvoiceVM> = Object.fromEntries(
              invEdits.map((x) => [idN(x.id), { ...x, id: idN(x.id) }])
            );
            const fields = ["platformId", "invoiceExternalId", "amountFiat", "amountUsdt", "platformRate"] as const;

            for (const before of invoices) {
              const after = afterById[before.id];
              if (!after) continue;
              if ((after as any)._deleted) {
                changedInvoices.push({ id: before.id, _op: OP.DELETE });
                continue;
              }
              if (!shallowEqual(before as any, after as any, fields as unknown as string[])) {
                changedInvoices.push({
                  id: before.id,
                  _op: OP.UPDATE,
                  platformId: after.platformId,
                  invoiceExternalId: (after.invoiceExternalId || "").trim(),
                  amountFiat: +after.amountFiat || 0,
                  amountUsdt: after.amountUsdt != null ? +after.amountUsdt : null,
                  platformRate: after.platformRate != null ? +after.platformRate : null,
                });
              }
            }

            for (const row of invEdits) {
              if ((row as any)?._new && !(row as any)?._deleted) {
                changedInvoices.push({
                  _op: OP.ADD,
                  platformId: row.platformId,
                  invoiceExternalId: (row.invoiceExternalId || "").trim(),
                  amountFiat: +row.amountFiat || 0,
                  amountUsdt: row.amountUsdt != null ? +row.amountUsdt : null,
                  platformRate: row.platformRate != null ? +row.platformRate : null,
                });
              }
            }

            const ok = await onApplyAllChanges(
              vm,
              orderPatch,
              changedInvoices,
              platforms,
              (orderEdit.comment ?? "").trim() || null,
              (orderEdit.tag ?? "").trim() || null,     
            );
            if (ok) setEditing(false);
          }}
        >
          Сохранить
        </Button>

        <Button
          onClick={() => {
            setEditing(false);
            setOrderEdit({
              exchangeOrderExternalId: vm.exchangeOrderExternalId || "",
              orderAmountFiat: vm.orderAmountFiat || 0,
              exchangeRate: vm.exchangeRate || 0,
              cardTopUp: vm.cardTopUp ?? 0,
              refund: vm.refund ?? 0,
              comment: vm.comment || "",
            });
            setInvEdits(invoices.map((x) => ({ ...x })));
          }}
        >
          Отмена
        </Button>
      </>
    )}

    <Popconfirm
      title="Удалить ордер?"
      description="Действительно хотите удалить этот ордер? Это действие необратимо."
      okText="Удалить"
      cancelText="Отмена"
      okButtonProps={{ danger: true }}
      onConfirm={() => onDeleteOrder(vm.id)}
    >
      <Button className="wo-delete-btn" danger>
        Удалить ордер
      </Button>
    </Popconfirm>
  </Space>

  {/* Комментарий сразу под кнопками (в том же grid-элементе) */}
  <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
  {editing ? (
    <div
      className={`wo-field wo-field-textarea ${
        (orderEdit.comment ?? "") !== (vm.comment ?? "") ? "wo-dirty" : ""
      }`}
      style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
    >
      <div className="wo-label">Комментарий</div>
      <Input.TextArea
    placeholder="Комментарий"
    style={{ marginTop: "20px" }}
    autoSize={{ minRows: 4, maxRows: 5 }}
    value={orderEdit.comment}
    onChange={(e) => setOrderEdit((s) => ({ ...s, comment: e.target.value }))}
    disabled={isClosed && !editing}
  />
    </div>
  ) : (
    <Input.TextArea
    placeholder="Комментарий"
    style={{ marginTop: "20px" }}
    autoSize={{ minRows: 4, maxRows: 5 }}
    value={orderEdit.comment}
    onChange={(e) => handleCommentChange(e.target.value)}
    disabled={isClosed && !editing}
  />
  )}
</div>
</div>

    </div>

    <Divider className="wo-divider" />

    <div className="wo-payments-title">Платежи</div>

    <Space direction="vertical" style={{ width: "100%" }}>
      {(editing ? invEdits : invoices).map((inv, idx) => renderInvoiceRow(editing, inv, idx))}

      {editing ? (
        <AddInvoiceInline
          vm={vm}
          platforms={platforms}
          onAdd={(draft, plat) => {
            const ext = (draft.invoiceId || "").trim();
            const dup = (invEdits || []).some(
              (r) =>
                !(r as any)._deleted &&
                r.platformId === draft.platformId &&
                (r.invoiceExternalId || "").trim().toLowerCase() === ext.toLowerCase()
            );
            if (dup) {
              message.warning("Такой инвойс уже есть в списке");
              return;
            }

            const eff = calcEffectiveRate({
              amountFiat: +(draft.amountFiat ?? 0) || 0,
              usdt: draft.usdt != null ? +draft.usdt : null,
              platformRate: draft.platformRate != null ? +draft.platformRate : null,
              markupPercent: getMarkup(plat),
              mode: getMode(plat) as InputMode,
            });
            const p2bb = percentToBb(eff ?? 0, +vm.exchangeRate || 0);

            const tempId = -Math.floor(Date.now() + Math.random() * 1000);

            setInvEdits((list) => [
              ...list,
              {
                id: tempId,
                orderId: vm.id,
                platformId: draft.platformId as number,
                invoiceExternalId: ext,
                amountFiat: +(draft.amountFiat ?? 0) || 0,
                amountUsdt: draft.usdt != null ? +draft.usdt : null,
                platformRate: draft.platformRate != null ? +draft.platformRate : null,
                effectiveRate: (eff || 0) as number,
                percentToBb: (p2bb || 0) as number,
                _new: true,
              } as InvoiceVM,
            ]);
          }}
        />
      ) : (
        canAddInvoice && <AddInvoiceRow vm={vm} platforms={platforms} onAddInvoice={onAddInvoice} />
      )}
    </Space>

    {/* НИЖНИЙ БАР */}
    {!editing && isOpen && (
      <div style={{ marginTop: 16 }}>
        <Row gutter={[8, 8]} wrap={false}>
          <Col flex="none">
            <Space>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Button
                  onClick={() => {
                    setShowTopup((s) => !s);
                    setShowRefund(false);
                  }}
                  icon={<PlusOutlined />}
                >
                  Добавить добив
                </Button>
                {showTopup && (
                  <>
                    <InputNumber
                      min={0}
                      step={0.01}
                      value={topupAmount as number | null}
                      parser={numParser}
                      onChange={setTopupAmount as any}
                      placeholder="Сумма, ₽"
                      style={{ width: 140 }}
                    />
                    <Tooltip title="Применить">
                      <Button
                        type="primary"
                        icon={<CheckOutlined />}
                        loading={busyTR}
                        onClick={async () => {
                          const amt = +topupAmount! || 0;
                          if (amt <= 0) {
                            message.warning("Введите сумму добива");
                            return;
                          }
                          try {
                            setBusyTR(true);
                            const res = await onAddTopup(vm, amt);
                            if (res) {
                              setTopupAmount(null);
                              setShowTopup(false);
                            }
                          } finally {
                            setBusyTR(false);
                          }
                        }}
                      />
                    </Tooltip>
                  </>
                )}
              </div>

              <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Button
                  onClick={() => {
                    setShowRefund((s) => !s);
                    setShowTopup(false);
                  }}
                  icon={<PlusOutlined />}
                >
                  Добавить возврат
                </Button>
                {showRefund && (
                  <>
                    <InputNumber
                      min={0}
                      step={0.01}
                      value={refundAmount as number | null}
                      parser={numParser}
                      onChange={setRefundAmount as any}
                      placeholder="Сумма, ₽"
                      style={{ width: 140 }}
                    />
                    <Tooltip title="Применить">
                      <Button
                        type="primary"
                        icon={<CheckOutlined />}
                        loading={busyTR}
                        onClick={async () => {
                          const amt = +refundAmount! || 0;
                          if (amt <= 0) {
                            message.warning("Введите сумму возврата");
                            return;
                          }
                          try {
                            setBusyTR(true);
                            const res = await onAddRefund(vm, amt);
                            if (res) {
                              setRefundAmount(null);
                              setShowRefund(false);
                            }
                          } finally {
                            setBusyTR(false);
                          }
                        }}
                      />
                    </Tooltip>
                  </>
                )}
              </div>
            </Space>
          </Col>

          <Col flex="auto">
            <Button
              type="primary"
              block
              onClick={() =>
                onCloseDraft(
                  vm,
                  invsForSummary,
                  aggregates.display,
                  (orderEdit.comment ?? vm.comment ?? "").trim() || null
                )
              }
            >
              Закрыть ордер
            </Button>
          </Col>
        </Row>
      </div>
    )}
  </Card>
);

}

/* ---------------- Добавление платежа ---------------- */

// Универсальный пересчёт черновика (для AddInvoiceRow / AddInvoiceInline)
function recalcDraft(
  draft: NewInvoiceDraft,
  platform: PlatformVM,
  changed: "amountFiat" | "usdt" | "platformRate"
): NewInvoiceDraft {
  const mode = getMode(platform || {}) as InputMode;
  const m = getMarkup(platform || {});
  const amount = +(draft.amountFiat ?? 0) || 0;
  let usdt = draft.usdt != null ? +draft.usdt : null;
  let rate = draft.platformRate != null ? +draft.platformRate : null;

  if (mode === "RateOnly") {
    if (changed === "amountFiat" || changed === "platformRate") {
      if (amount > 0 && (rate ?? 0) > 0) usdt = usdtFromRate(amount, rate);
    }
  } else if (mode === "AmountUsdtOnly") {
    if (changed === "amountFiat" || changed === "usdt") {
      if (amount > 0 && (usdt ?? 0) > 0) rate = rateFromUsdt(amount, usdt, m);
    }
  } else {
    if (changed === "usdt") {
      if (amount > 0 && (usdt ?? 0) > 0) rate = rateFromUsdt(amount, usdt, m);
    } else if (changed === "platformRate") {
      if (amount > 0 && (rate ?? 0) > 0) usdt = usdtFromRate(amount, rate);
    } else if (changed === "amountFiat") {
      if ((usdt ?? null) != null && !(rate! > 0)) rate = rateFromUsdt(amount, usdt, m);
      else if ((rate ?? null) != null && !(usdt! > 0)) usdt = usdtFromRate(amount, rate);
    }
  }

  return { ...draft, usdt, platformRate: rate };
}

function AddInvoiceRow({
  vm,
  platforms,
  onAddInvoice,
}: {
  vm: OrderVM;
  platforms: PlatformVM[];
  onAddInvoice: (vm: OrderVM, draft: NewInvoiceDraft, platform: PlatformVM) => Promise<void>;
}) {
  const [draft, setDraft] = useState<NewInvoiceDraft>({
    amountFiat: null,
    platformId: null,
    invoiceId: "",
    usdt: null,
    platformRate: null,
  });

  const plat = (platforms.find((p) => p.id === draft.platformId) || {}) as PlatformVM;
  const mode = getMode(plat) as InputMode;

  return (
    <Row gutter={[8, 8]} align="middle" className="wo-add-payline">
      <Col xs={24} className="wo-col-grow">
        <InputNumber
          placeholder="Сумма инвойса"
          min={0}
          step={0.01}
          style={{ width: "100%" }}
          value={draft.amountFiat as number | null}
          parser={numParser}
          onChange={(v) => {
            const next = recalcDraft({ ...draft, amountFiat: v as number | null }, plat, "amountFiat");
            setDraft(next);
          }}
        />
      </Col>

      <Col xs={24} className="wo-col-grow">
        <Select
          placeholder="Платформа"
          value={draft.platformId as number | null}
          style={{ width: "100%" }}
          options={(platforms || []).map((p) => ({ value: p.id, label: p.name ?? p.id }))}
          showSearch
          optionFilterProp="label"
          onChange={(v) => setDraft((s) => ({ ...s, platformId: v as number }))}
        />
      </Col>

      <Col xs={24} className="wo-col-grow">
        <Input
          placeholder="ID инвойса"
          value={draft.invoiceId}
          onChange={(e) => setDraft((s) => ({ ...s, invoiceId: e.target.value }))}
        />
      </Col>

      <Col xs={24} md={4}>
        <InputNumber
          placeholder="USDT"
          min={0}
          step={0.0001}
          style={{ width: "100%" }}
          disabled={mode === "RateOnly"}
          value={draft.usdt as number | null}
          parser={numParser}
          onChange={(v) => {
            const next = recalcDraft({ ...draft, usdt: v as number | null }, plat, "usdt");
            setDraft(next);
          }}
        />
      </Col>

      <Col xs={24} md={4}>
        <InputNumber
          placeholder="Курс площадки"
          min={0}
          step={0.0001}
          style={{ width: "100%" }}
          disabled={mode === "AmountUsdtOnly"}
          value={draft.platformRate as number | null}
          parser={numParser}
          onChange={(v) => {
            const next = recalcDraft(
              { ...draft, platformRate: v as number | null },
              plat,
              "platformRate"
            );
            setDraft(next);
          }}
        />
      </Col>

      <Col xs={24} className="wo-col-btn">
        <Button
          type="primary"
          block
          disabled={!draft.amountFiat || !draft.platformId || (!draft.usdt && !draft.platformRate)}
          onClick={() => {
            onAddInvoice(vm, draft, plat);
            setDraft({
              amountFiat: null,
              platformId: null,
              invoiceId: "",
              usdt: null,
              platformRate: null,
            });
          }}
          style={{ whiteSpace: "nowrap" }}
        >
          Добавить платёж
        </Button>
      </Col>
    </Row>
  );
}

/* ------- Инлайн-добавление для режима редактирования ------- */
function AddInvoiceInline({
  vm,
  platforms,
  onAdd,
}: {
  vm: OrderVM;
  platforms: PlatformVM[];
  onAdd: (draft: NewInvoiceDraft, platform: PlatformVM) => void;
}) {
  const [draft, setDraft] = useState<NewInvoiceDraft>({
    amountFiat: null,
    platformId: null,
    invoiceId: "",
    usdt: null,
    platformRate: null,
  });

  const plat = (platforms.find((p) => p.id === draft.platformId) || {}) as PlatformVM;
  const mode = getMode(plat) as InputMode;

  return (
    <Row gutter={[8, 8]} align="middle" className="wo-add-payline">
      <Col xs={24} className="wo-col-grow">
        <InputNumber
          placeholder="Сумма инвойса"
          min={0}
          step={0.01}
          style={{ width: "100%" }}
          value={draft.amountFiat as number | null}
          parser={numParser}
          onChange={(v) => setDraft((s) => recalcDraft({ ...s, amountFiat: v as number | null }, plat, "amountFiat"))}
        />
      </Col>

      <Col xs={24} className="wo-col-grow">
        <Select
          placeholder="Платформа"
          value={draft.platformId as number | null}
          style={{ width: "100%" }}
          options={(platforms || []).map((p) => ({ value: p.id, label: p.name ?? p.id }))}
          showSearch
          optionFilterProp="label"
          onChange={(v) => setDraft((s) => ({ ...s, platformId: v as number }))}
        />
      </Col>

      <Col xs={24} className="wo-col-grow">
        <Input
          placeholder="ID инвойса"
          value={draft.invoiceId}
          onChange={(e) => setDraft((s) => ({ ...s, invoiceId: e.target.value }))}
        />
      </Col>

      <Col xs={24} md={4}>
        <InputNumber
          placeholder="USDT"
          min={0}
          step={0.0001}
          style={{ width: "100%" }}
          disabled={mode === "RateOnly"}
          value={draft.usdt as number | null}
          parser={numParser}
          onChange={(v) => setDraft((s) => recalcDraft({ ...s, usdt: v as number | null }, plat, "usdt"))}
        />
      </Col>

      <Col xs={24} md={4}>    
        <InputNumber
          placeholder="Курс площадки"
          min={0}
          step={0.0001}
          style={{ width: "100%" }}
          disabled={mode === "AmountUsdtOnly"}
          value={draft.platformRate as number | null}
          parser={numParser}
          onChange={(v) => setDraft((s) => recalcDraft({ ...s, platformRate: v as number | null }, plat, "platformRate"))}
        />
      </Col>

      <Col xs={24} className="wo-col-btn">
        <Button
          type="primary"
          block
          disabled={
            !draft.amountFiat ||
            !draft.platformId ||
            (!draft.usdt && !draft.platformRate) ||
            !(draft.invoiceId || "").trim()
          }
          onClick={() => {
            onAdd(draft, plat);
            setDraft({
              amountFiat: null,
              platformId: null,
              invoiceId: "",
              usdt: null,
              platformRate: null,
            });
          }}
          style={{ whiteSpace: "nowrap" }}
        >
          Добавить платёж
        </Button>
      </Col>
    </Row>
  );
}
/* ---------------- страница ---------------- */

export default function WorkerOrdersPage() {
  const { message } = AntdApp.useApp();
  const { user } = useAuth() || {};
  const workerId = +((user as any)?.id || (user as any)?.sub || 0);
  const officeId = +((user as any)?.officeId || 0);

  const [bybitId, setBybitId] = useState<number | null>(null);
  const [platforms, setPlatforms] = useState<PlatformVM[]>([]);

  // Закреплённые
  const [pinnedIds, setPinnedIds] = useState<Set<number>>(new Set());
  const [pinnedVM, setPinnedVM] = useState<OrderVM[]>([]);

  // Постранично с сервера
  const [pagedVM, setPagedVM] = useState<OrderVM[]>([]);
  const pageSize = 2;
  const [cursor, setCursor] = useState<{ skip: number; take: number }>({ skip: 0, take: pageSize });
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Чтобы исключать дубли
  const [seenIds, setSeenIds] = useState<Set<number>>(new Set());

  // Общее
  const [invoicesMap, setInvoicesMap] = useState<Record<number, InvoiceVM[]>>({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  
  const [searchVM, setSearchVM] = useState<OrderVM[]>([]);
  const [searching, setSearching] = useState(false);
  const searchPageSize = 5;
  const [searchCursor, setSearchCursor] = useState<{ skip: number; take: number }>({
    skip: 0,
    take: searchPageSize,
  });
  const [searchHasMore, setSearchHasMore] = useState(false);
  const [searchLoadingMore, setSearchLoadingMore] = useState(false);

  const [form, setForm] = useState<{
   amount: number | null;
   exchangeRate: number | null;
   externalId: string;
   tag?: string | null;
 }>({
   amount: null,
   exchangeRate: null,
   externalId: "",
   tag: "",
 });

  const loaderRef = useRef<HTMLDivElement | null>(null);
  const canceledNames = new Set(["canceled", "cancelled", "cancel"]);

  /* ---- INITIAL LOAD ---- */
  useEffect(() => {
    document.title = "Главная — Jetrub";
    const setFav = (rel: string, href: string) => {
      let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement("link");
        link.rel = rel;
        document.head.appendChild(link);
      }
      link.type = "image/png";
      link.sizes = "32x32";
      link.href = href;
    };
    setFav("icon", faviconUrl);
    setFav("shortcut icon", faviconUrl);

    let alive = true;
    async function loadInitial() {
      try {
        setLoading(true);

        const [plats, exchs] = await Promise.all([getPlatforms(), getExchanges()]);
        if (!alive) return;
        setPlatforms((plats || []) as PlatformVM[]);

        const bybit = (exchs as any[] | undefined)?.find(
          (e) =>
            (e?.code || "").toLowerCase() === "bybit" ||
            (e?.name || "").toLowerCase().includes("bybit")
        );
        setBybitId(bybit?.id ?? 1);

        // 1) пины — ПЕРВЫМИ
        const pinsRaw = (await getMyPinnedOrders(workerId)) as any[];
        const pinOrdersRaw = (pinsRaw || []).filter(
          (o: any) =>
            !canceledNames.has((o.statusName || "").toLowerCase()) && (o.statusId ?? o.status) !== 4
        );
        const pinOrders = pinOrdersRaw.map(toVM).sort(sortByExecutedDesc);

        const pinSet = new Set(pinOrders.map((x: any) => x.id));
        if (!alive) return;
        setPinnedIds(pinSet as Set<number>);
        setPinnedVM(pinOrders);
        setSeenIds(new Set(pinSet) as Set<number>);

        // инвойсы для пинов — из pinsRaw
        if (pinOrdersRaw.length) {
          const invMapPins = buildInvMapFromApiOrders(pinOrdersRaw);
          if (alive) setInvoicesMap((prev) => ({ ...prev, ...invMapPins }));
        }

        // 2) первая страница остальных
        await loadMore(false, new Set(pinSet) as Set<number>);
      } catch (e) {
        console.error(e);
        message.error("Не удалось загрузить данные");
      } finally {
        if (alive) setLoading(false);
      }
    }
    if (workerId) loadInitial();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerId]);

  /* ---- INFINITE SCROLL OBSERVER ---- */
  useEffect(() => {
  if (!loaderRef.current) return;
  const el = loaderRef.current;

  const io = new IntersectionObserver(
    (entries) => {
      const [entry] = entries;
      if (!entry.isIntersecting) return;

      const hasQuery = !!(q || "").trim();

      if (!hasQuery) {
        // обычный список
        if (!loading && !loadingMore && hasMore) {
          loadMore(false, pinnedIds);
        }
      } else {
        // поиск
        if (!searching && !searchLoadingMore && searchHasMore) {
          loadMoreSearch();
        }
      }
    },
    { root: null, rootMargin: "300px", threshold: 0 }
  );

  io.observe(el);
  return () => io.disconnect();
}, [
  q,
  pinnedIds,
  loading,
  loadingMore,
  hasMore,
  searching,
  searchLoadingMore,
  searchHasMore,
]);

  async function loadMore(isReset: boolean, pinnedSetArg?: Set<number>) {
    try {
      setLoadingMore(true);

      const nextCursor = isReset ? { skip: 0, take: pageSize } : cursor;
      const res: any = await getMyOrdersPaged(nextCursor.skip, nextCursor.take);

      const itemsRawAll = (res as any)?.items ?? (res as any)?.Items ?? [];
      const hasMoreRaw = (res as any)?.hasMore ?? (res as any)?.HasMore ?? false;

      const baseFilter = (o: any) =>
        !((o.statusId ?? o.status) === 4 || canceledNames.has((o.statusName || "").toLowerCase()));

      const pinnedSet = pinnedSetArg ?? pinnedIds;

      const itemsFilteredRaw = (itemsRawAll as any[])
        .filter(baseFilter)
        .filter((o: any) => !pinnedSet.has(idN(o.id)))
        .filter((o: any) => !seenIds.has(idN(o.id)));

      const newItems = itemsFilteredRaw.map(toVM);

      setPagedVM((prev) => (isReset ? newItems : [...prev, ...newItems]).sort(sortByExecutedDesc));
      setCursor({ skip: nextCursor.skip + nextCursor.take, take: nextCursor.take });
      setHasMore(!!hasMoreRaw);

      if (newItems.length) {
        const invMap = buildInvMapFromApiOrders(itemsFilteredRaw);
        setInvoicesMap((prev) => ({ ...prev, ...invMap }));
        setSeenIds((prev) => {
          const next = new Set(prev);
          newItems.forEach((x: any) => next.add(x.id));
          return next as Set<number>;
        });
      }
    } catch (e) {
      console.error(e);
      message.error("Не удалось догрузить ордера");
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }
  /* ---- SERVER SEARCH WITH DEBOUNCE ---- */
useEffect(() => {
  let alive = true;
  const qTrim = (q || "").trim();

  if (!qTrim) {
    // пустой ввод — очищаем режим поиска
    setSearchVM([]);
    setSearching(false);
    setSearchHasMore(false);
    setSearchCursor({ skip: 0, take: searchPageSize });
    return;
  }

  setSearching(true);
  const timer = setTimeout(async () => {
    try {
      // первая страница
      const { items, hasMore } = await searchMyOrdersPaged<any>(qTrim, 0, searchPageSize);
      if (!alive) return;

      setSearchVM((items || []).map(toVM));
      const invMap = buildInvMapFromApiOrders(items as any[]);
      setInvoicesMap(prev => ({ ...prev, ...invMap }));

      setSearchCursor({ skip: searchPageSize, take: searchPageSize });
      setSearchHasMore(!!hasMore);
    } catch (e) {
      console.error(e);
    } finally {
      alive && setSearching(false);
    }
  }, 400); // debounce

  return () => { alive = false; clearTimeout(timer); };
}, [q]);
async function loadMoreSearch() {
  if (searchLoadingMore) return;
  try {
    setSearchLoadingMore(true);
    const { skip, take } = searchCursor;
    const { items, hasMore } = await searchMyOrdersPaged<any>((q || "").trim(), skip, take);

    const newItemsRaw = items || [];
    if (newItemsRaw.length) {
      const invMap = buildInvMapFromApiOrders(newItemsRaw as any[]);
      setInvoicesMap(prev => ({ ...prev, ...invMap }));

      const newVMs = newItemsRaw.map(toVM);
      setSearchVM(prev => [...prev, ...newVMs].sort(sortByExecutedDesc));
      setSearchCursor({ skip: skip + take, take });
    }
    setSearchHasMore(!!hasMore);
  } catch (e) {
    console.error(e);
    setSearchHasMore(false);
  } finally {
    setSearchLoadingMore(false);
  }
}


  /* ---- SEARCH ---- */
  const list = useMemo(() => {
  const hasQuery = !!(q || "").trim();
  if (hasQuery) return searchVM;
  return [...pinnedVM, ...pagedVM];
}, [q, searchVM, pinnedVM, pagedVM]);

  /* ---- HANDЛЕРЫ ---- */

  async function handleAddOrder() {
    try {
      message.loading({ content: "Создаём ордер…", key: "add" });
      const { amount, exchangeRate, externalId } = form;
      if (!amount || !exchangeRate) {
        message.warning({ content: "Заполните сумму и курс", key: "add" });
        return;
      }

      const req = {
        officeId,
        workerId,
        exchangeId: bybitId ?? 1,
        exchangeOrderExternalId: (externalId || `ORD-${Date.now()}`).trim(),
        orderAmountFiat: +amount!,
        exchangeRate: +exchangeRate!,
        executedAt: new Date().toISOString(),
        comment: null,
        tag: (form.tag || "").trim() || null,
        cardTopUp: 0,
        refund: 0,
      };

      const res: any = await saveOrder(req);
      if (!res.ok || !res.order) {
        message.error({ content: res.error || "Не удалось создать ордер", key: "add" });
        return;
      }

      const dto = res.order;
      const vm = toVM(dto);
      const invs = ((dto as any).Invoices ?? (dto as any).invoices ?? []).map(toInvoice) as InvoiceVM[];
      setInvoicesMap((prev) => ({ ...prev, [vm.id]: invs }));

      setPagedVM((prev) => [vm, ...prev].sort(sortByExecutedDesc));

      setSeenIds((prev) => new Set(prev).add(vm.id) as Set<number>);
      setForm({ amount: null, exchangeRate: null, externalId: "", tag: "" });
      message.success({ content: "Ордер добавлен", key: "add" });
    } catch (e) {
      console.error(e);
      message.error({ content: "Ошибка при добавлении ордера", key: "add" });
    }
  }

  async function handleApplyAllChanges(
  vm: OrderVM,
  orderPatch: OrderPatch,
  invoicePatches: InvoicePatch[],
  platforms: PlatformVM[],
  nextComment: string | null,
  nextTag: string | null,             // <— приняли комментарий
) {
  try {
    const bbRate =
      +((orderPatch.exchangeRate != null ? orderPatch.exchangeRate : vm.exchangeRate) as number) || 0;

    const currentInvs = [...(invoicesMap[vm.id] || [])];

    const delIds = new Set(
      invoicePatches.filter((p) => p._op === OP.DELETE).map((p) => idN((p as InvoicePatchDelete).id))
    );
    const survivors = currentInvs.filter((inv) => !delIds.has(inv.id));

    const updated = survivors.map((inv) => {
      const p = invoicePatches.find((x): x is InvoicePatchUpdate => x._op === OP.UPDATE && (x as any).id === inv.id);
      if (!p) return inv;

      const platform = platforms.find((x) => x.id === (p.platformId ?? inv.platformId)) || ({} as PlatformVM);
      const mode = getMode(platform) as InputMode;

      const amountFiat = p.amountFiat != null ? +p.amountFiat : inv.amountFiat;
      const amountUsdt = p.amountUsdt != null ? +p.amountUsdt! : inv.amountUsdt;
      const platformRate = p.platformRate != null ? +p.platformRate! : inv.platformRate;

      const eff = calcEffectiveRate({
        amountFiat,
        usdt: amountUsdt ?? null,
        platformRate: platformRate ?? null,
        markupPercent: getMarkup(platform),
        mode: mode as InputMode,
      });

      const p2bb = percentToBb(eff ?? 0, bbRate);

      return {
        ...inv,
        platformId: p.platformId ?? inv.platformId,
        invoiceExternalId: (p.invoiceExternalId ?? inv.invoiceExternalId) || "",
        amountFiat,
        amountUsdt,
        platformRate,
        effectiveRate: (eff || 0) as number,
        percentToBb: (p2bb || 0) as number,
      } as InvoiceVM;
    });

    const addedLocal = invoicePatches
      .filter((p): p is InvoicePatchAdd => p._op === OP.ADD)
      .map((p) => {
        const platform = platforms.find((x) => x.id === p.platformId) || ({} as PlatformVM);
        const mode = getMode(platform) as InputMode;
        const eff = calcEffectiveRate({
          amountFiat: +p.amountFiat || 0,
          usdt: p.amountUsdt != null ? +p.amountUsdt : null,
          platformRate: p.platformRate != null ? +p.platformRate : null,
          markupPercent: getMarkup(platform),
          mode,
        });
        const p2bb = percentToBb(eff ?? 0, bbRate);
        return {
          id: -Math.floor(Date.now() + Math.random() * 1000),
          orderId: vm.id,
          platformId: p.platformId,
          invoiceExternalId: (p.invoiceExternalId || "").trim(),
          amountFiat: +p.amountFiat || 0,
          amountUsdt: p.amountUsdt != null ? +p.amountUsdt : null,
          platformRate: p.platformRate != null ? +p.platformRate : null,
          effectiveRate: (eff || 0) as number,
          percentToBb: (p2bb || 0) as number,
          _new: true,
        } as InvoiceVM;
      });

    const nextInvs = [...updated, ...addedLocal];

    const vmForSummary: OrderVM = {
      ...vm,
      orderAmountFiat: (orderPatch.orderAmountFiat ?? vm.orderAmountFiat) as number,
      exchangeRate: bbRate,
      cardTopUp: (orderPatch.cardTopUp ?? vm.cardTopUp ?? 0) as number,
      refund: (orderPatch.refund ?? vm.refund ?? 0) as number,
    };
    const agg = calcOrderAggregates(vmForSummary, nextInvs, AGG_OPTS);

    // «тупо» кладём текущий комментарий в запрос независимо от статуса/изменения
    const req: any = {
      ExchangeOrderExternalId: orderPatch.exchangeOrderExternalId ?? undefined,
      OrderAmountFiat: orderPatch.orderAmountFiat ?? undefined,
      ExchangeRate: orderPatch.exchangeRate ?? undefined,
      TopupFromCard: orderPatch.cardTopUp ?? undefined,
      RefundOverpay: orderPatch.refund ?? undefined,
      AvgPercent: agg.avgPercent,
      Profit: agg.display,
      Comment: (nextComment ?? "").trim() || null,
      Tag: (nextTag ?? "").trim() || null,    // <— вот оно
      Invoices: invoicePatches.length
        ? invoicePatches.map((p) => {
            if (p._op === OP.DELETE) {
              return { Op: OP.DELETE, Id: idN((p as InvoicePatchDelete).id) };
            }
            if (p._op === OP.UPDATE) {
              const u = p as InvoicePatchUpdate;
              const platform = platforms.find((x) => x.id === (u.platformId ?? 0)) || ({} as PlatformVM);
              const mode = getMode(platform) as InputMode;

              const eff = calcEffectiveRate({
                amountFiat: +(u.amountFiat ?? 0) || 0,
                usdt: u.amountUsdt != null ? +u.amountUsdt : null,
                platformRate: u.platformRate != null ? +u.platformRate : null,
                markupPercent: getMarkup(platform),
                mode,
              });
              const p2bb = percentToBb(eff ?? 0, bbRate);

              return {
                Op: OP.UPDATE,
                Id: idN(u.id),
                PlatformId: u.platformId,
                InvoiceExternalId: (u.invoiceExternalId || "").trim(),
                AmountFiat: +(u.amountFiat ?? 0) || 0,
                PlatformRate: u.platformRate != null ? +u.platformRate : null,
                AmountUsdt: u.amountUsdt != null ? +u.amountUsdt : null,
                EffectiveRate: (eff || 0) as number,
                PercentToBb: (p2bb || 0) as number,
              };
            }
            {
              const a = p as InvoicePatchAdd;
              const platform = platforms.find((x) => x.id === a.platformId) || ({} as PlatformVM);
              const mode = getMode(platform) as InputMode;
              const eff = calcEffectiveRate({
                amountFiat: +a.amountFiat || 0,
                usdt: a.amountUsdt != null ? +a.amountUsdt : null,
                platformRate: a.platformRate != null ? +a.platformRate : null,
                markupPercent: getMarkup(platform),
                mode,
              });
              const p2bb = percentToBb(eff ?? 0, bbRate);

              return {
                Op: OP.ADD,
                PlatformId: a.platformId,
                InvoiceExternalId: (a.invoiceExternalId || "").trim(),
                AmountFiat: +a.amountFiat || 0,
                PlatformRate: a.platformRate != null ? +a.platformRate : null,
                AmountUsdt: a.amountUsdt != null ? +a.amountUsdt : null,
                EffectiveRate: (eff || 0) as number,
                PercentToBb: (p2bb || 0) as number,
                PaidAt: new Date().toISOString(),
              };
            }
          })
        : undefined,
    };

    Object.keys(req).forEach((k) => req[k] === undefined && delete req[k]);

    const updRes = await updateOrder(vm.id, req);
    if (!updRes.ok) {
      message.error(updRes.error || "Не удалось сохранить изменения ордера");
      return false;
    }

    // Локальный патч (включая комментарий)
    const vmPatch: Partial<OrderVM> = {
      exchangeOrderExternalId: (req.ExchangeOrderExternalId ?? vm.exchangeOrderExternalId) as string,
      orderAmountFiat: (req.OrderAmountFiat ?? vm.orderAmountFiat) as number,
      exchangeRate: (req.ExchangeRate ?? vm.exchangeRate) as number,
      cardTopUp: (req.TopupFromCard ?? vm.cardTopUp ?? 0) as number,
      refund: (req.RefundOverpay ?? vm.refund ?? 0) as number,
      comment: req.Comment ?? null,
      tag: req.Tag ?? vm.tag ?? null,   // <— обновили
    };

    const upd = (arr: OrderVM[]) =>
      [...arr].map((o) => (o.id === vm.id ? ({ ...o, ...vmPatch } as OrderVM) : o)).sort(sortByExecutedDesc);
    setPinnedVM(upd);
    setPagedVM(upd);
    setSearchVM((arr) =>
      [...arr].map((o) => (o.id === vm.id ? ({ ...o, ...vmPatch } as OrderVM) : o)).sort(sortByExecutedDesc)
    );

    // Инвойсы
    setInvoicesMap((map) => {
      let arr = [...(map[vm.id] || [])];

      if (delIds.size) arr = arr.filter((x) => !delIds.has(x.id));

      invoicePatches
        .filter((p): p is InvoicePatchUpdate => p._op === OP.UPDATE)
        .forEach((p) => {
          const idx = arr.findIndex((x) => idN(x.id) === idN(p.id));
          if (idx >= 0) {
            const platform = platforms.find((x) => x.id === (p.platformId ?? arr[idx].platformId)) || ({} as PlatformVM);
            const mode = getMode(platform) as InputMode;

            const amountFiat = p.amountFiat != null ? +p.amountFiat : arr[idx].amountFiat;
            const amountUsdt = p.amountUsdt != null ? +p.amountUsdt! : arr[idx].amountUsdt;
            const platformRate = p.platformRate != null ? +p.platformRate! : arr[idx].platformRate;

            const eff = calcEffectiveRate({
              amountFiat,
              usdt: amountUsdt ?? null,
              platformRate: platformRate ?? null,
              markupPercent: getMarkup(platform),
              mode,
            });
            const p2bb = percentToBb(eff ?? 0, bbRate);

            arr[idx] = {
              ...arr[idx],
              platformId: p.platformId ?? arr[idx].platformId,
              invoiceExternalId: (p.invoiceExternalId ?? arr[idx].invoiceExternalId) || "",
              amountFiat,
              amountUsdt,
              platformRate,
              effectiveRate: (eff || 0) as number,
              percentToBb: (p2bb || 0) as number,
            } as InvoiceVM;
          }
        });

      invoicePatches
        .filter((p): p is InvoicePatchAdd => p._op === OP.ADD)
        .forEach((p) => {
          const platform = platforms.find((x) => x.id === p.platformId) || ({} as PlatformVM);
          const mode = getMode(platform) as InputMode;
          const eff = calcEffectiveRate({
            amountFiat: +p.amountFiat || 0,
            usdt: p.amountUsdt != null ? +p.amountUsdt : null,
            platformRate: p.platformRate != null ? +p.platformRate : null,
            markupPercent: getMarkup(platform),
            mode,
          });
          const p2bb = percentToBb(eff ?? 0, bbRate);

          arr.push({
            id: -Math.floor(Date.now() + Math.random() * 1000),
            orderId: vm.id,
            platformId: p.platformId,
            invoiceExternalId: (p.invoiceExternalId || "").trim(),
            amountFiat: +p.amountFiat || 0,
            amountUsdt: p.amountUsdt != null ? +p.amountUsdt : null,
            platformRate: p.platformRate != null ? +p.platformRate : null,
            effectiveRate: (eff || 0) as number,
            percentToBb: (p2bb || 0) as number,
          } as InvoiceVM);
        });

      return { ...map, [vm.id]: arr };
    });

    message.success("Изменения сохранены");
    return true;
  } catch (e) {
    console.error(e);
    message.error("Не удалось сохранить изменения");
    return false;
  }
}

  async function handleDeleteInvoice(orderId: number, invoiceId: number) {
    try {
      await deleteInvoice(invoiceId);
      const m = { ...invoicesMap };
      m[idN(orderId)] = (m[idN(orderId)] || []).filter((x: InvoiceVM) => x.id !== invoiceId);
      setInvoicesMap(m);
      message.success("Инвойс удалён");
    } catch {
      message.error("Нет прав удалить инвойс или ошибка сервера");
    }
  }

  async function handleDeleteOrder(orderId: number) {
    try {
      await deleteOrder(orderId);
      setPinnedVM((arr) => arr.filter((o) => o.id !== idN(orderId)));
      setPagedVM((arr) => arr.filter((o) => o.id !== idN(orderId)));
      setInvoicesMap((map) => {
        const m = { ...map };
        delete m[idN(orderId)];
        return m;
      });
      setPinnedIds((prev) => {
        const next = new Set(prev);
        next.delete(idN(orderId));
        return next;
      });
      setSeenIds((prev) => {
        const next = new Set(prev);
        next.delete(idN(orderId));
        return next;
      });
      message.success("Ордер удалён");
    } catch {
      message.error("Нет прав удалить ордер или ошибка сервера");
    }
  }

  async function handleCloseDraft(vm: OrderVM, invs: InvoiceVM[], displayProfit: number,comment: string | null) {
  try {
    const agg = calcOrderAggregates(vm, invs, { average: "simple", gateByCoverage: true });

    const res: any = await closeOrder({
      orderId: vm.id,
      avgPercent: agg.avgPercent,
      profit: displayProfit,
      comment: comment,
    });

    if (!res?.ok) {
      message.error("Не удалось закрыть ордер");
      return;
    }
    
    const patch: Partial<OrderVM> = { isDraft: false, statusId: 3, statusName: "Closed", comment };
    setPinnedVM(arr => arr.map(x => x.id === vm.id ? ({ ...x, ...patch } as OrderVM) : x));
    setPagedVM(arr => arr.map(x => x.id === vm.id ? ({ ...x, ...patch } as OrderVM) : x));
    message.success("Ордер закрыт");
  } catch {
    message.error("Ошибка при закрытии ордера");
  }
}

  async function handleAddInvoice(orderVM: OrderVM, draft: NewInvoiceDraft, platform: PlatformVM) {
    try {
      const eff = calcEffectiveRate({
        amountFiat: +(draft.amountFiat ?? 0),
        usdt: draft.usdt ? +draft.usdt : null,
        platformRate: draft.platformRate ? +draft.platformRate : null,
        markupPercent: getMarkup(platform),
        mode: getMode(platform) as InputMode,
      });
      const p2bb = percentToBb(eff ?? 0, +orderVM.exchangeRate || 0);

      const res: any = await addInvoice({
        orderId: orderVM.id,
        platformId: draft.platformId!,
        invoiceExternalId: (draft.invoiceId || "").trim(),
        amountFiat: +(draft.amountFiat ?? 0),
        platformRate: draft.platformRate ? +draft.platformRate : null,
        amountUsdt: draft.usdt ? +draft.usdt : null,
        effectiveRate: (eff || 0) as number,
        percentToBb: (p2bb || 0) as number,
        paidAt: new Date().toISOString(),
        note: null,
      });
      if (!res.ok) {
  message.error(res.error);
  return;
      }

      setInvoicesMap((map) => {
        const arr = [...(map[orderVM.id] || [])];
        arr.push({
          id: res.id as number,
          orderId: orderVM.id,
          platformId: draft.platformId!,
          invoiceExternalId: (draft.invoiceId || "").trim(),
          amountFiat: +(draft.amountFiat ?? 0),
          amountUsdt: draft.usdt ? +draft.usdt : null,
          platformRate: draft.platformRate ? +draft.platformRate : null,
          effectiveRate: eff as number,
          percentToBb: p2bb as number,
        } as InvoiceVM);
        return { ...map, [orderVM.id]: arr };
      });

      message.success("Платёж добавлен");
    } catch {
      message.error("Ошибка при добавлении платежа");
    }
  }

  // инлайн добив
  async function handleAddTopup(vm: OrderVM, amount: number) {
    try {
      const res: any = await addTopup({ orderId: vm.id, amount: +amount });
      if (!res.ok) {
        message.error(res.error || "Не удалось добавить добив");
        return false;
      }
      const patch: Partial<OrderVM> = { cardTopUp: (vm.cardTopUp || 0) + +amount };
      const upd = (arr: OrderVM[]) =>
        [...arr].map((o) => (o.id === vm.id ? ({ ...o, ...patch } as OrderVM) : o)).sort(sortByExecutedDesc);
      setPinnedVM(upd);
      setPagedVM(upd);
      message.success("Добив добавлен");
      return true;
    } catch {
      message.error("Ошибка при добавлении добива");
      return false;
    }
  }

  // инлайн возврат
  async function handleAddRefund(vm: OrderVM, amount: number) {
    try {
      const res: any = await addRefund({ orderId: vm.id, amount: +amount });
      if (!res.ok) {
        message.error(res.error || "Не удалось добавить возврат");
        return false;
      }
      const patch: Partial<OrderVM> = { refund: (vm.refund || 0) + +amount };
      const upd = (arr: OrderVM[]) =>
        [...arr].map((o) => (o.id === vm.id ? ({ ...o, ...patch } as OrderVM) : o)).sort(sortByExecutedDesc);
      setPinnedVM(upd);
      setPagedVM(upd);
      message.success("Возврат добавлен");
      return true;
    } catch {
      message.error("Ошибка при добавлении возврата");
      return false;
    }
  }

  async function handleTogglePin(orderId: number, currentlyPinned: boolean) {
  const oid = idN(orderId);

  try {
    if (currentlyPinned) {
      // ---- Открепление ----
      const ok = await unpinOrder(oid);
      if (!ok) throw new Error("unpin failed");

      // кого переносим
      const victim = pinnedVM.find(o => o.id === oid);

      setPinnedIds(prev => {
        const next = new Set(prev);
        next.delete(oid);
        return next;
      });

      // 1) убрать из пинов
      setPinnedVM(prev => prev.filter(o => o.id !== oid));

      // 2) добавить в обычные
      if (victim) {
        setPagedVM(prev => [...prev, victim].sort(sortByExecutedDesc));
      }

      setSeenIds(prev => new Set(prev).add(oid));
      message.success("Ордер откреплён");
    } else {
      // ---- Закрепление ----
      const ok = await pinOrder(oid);
      if (!ok) throw new Error("pin failed");

      // кого переносим
      const victim = pagedVM.find(o => o.id === oid);

      setPinnedIds(prev => new Set(prev).add(oid));

      // 1) убрать из обычных
      setPagedVM(prev => prev.filter(o => o.id !== oid));

      // 2) добавить в пины
      if (victim) {
        setPinnedVM(prev => [...prev, victim].sort(sortByExecutedDesc));
      }

      setSeenIds(prev => new Set(prev).add(oid));
      message.success("Ордер закреплён");
    }
  } catch (e) {
    console.error(e);
    message.error("Не удалось изменить закрепление");
  }
}

  /* ---- RENDER ---- */

  return (
    <Spin spinning={loading} size="large">
      <div className="wo-page">
<section className="wo-add">
  
    <Title level={3} className="wo-page-h">
      Добавить новый ордер
    </Title>
  <div className="wo-panel">

    {/* Сетка без добива/возврата */}
    <div className="wo-add-grid">
      <InputNumber
        className="wo-input"
        placeholder="Сумма ордера"
        value={form.amount as number | null}
        min={0}
        step={0.01}
        parser={numParser}
        onChange={(v) => setForm((s) => ({ ...s, amount: v as number | null }))}
      />
      <Input
        className="wo-input"
        placeholder="ID сделки"
        value={form.externalId}
        onChange={(e) => setForm((s) => ({ ...s, externalId: e.target.value }))}
      />

      <InputNumber
        className="wo-input"
        placeholder="Курс"
        value={form.exchangeRate as number | null}
        min={0}
        step={0.0001}
        parser={numParser}
        onChange={(v) => setForm((s) => ({ ...s, exchangeRate: v as number | null }))}
      />
      <Input
    className="wo-input"
    placeholder="Тег"
    value={form.tag ?? ""}
    onChange={(e) => setForm((s) => ({ ...s, tag: e.target.value }))}
  />


      <Button type="primary" className="wo-add-btn" onClick={handleAddOrder}>
        Добавить ордер
      </Button>
    </div>
  </div>
</section>


        <section className="wo-list">
          <div className="wo-list-head">
            <Title level={4} className="wo-section-title">
              Список ордеров
            </Title>
            <Input
              className="wo-search"
              allowClear
              placeholder="Искать по Order ID, ID сделки на бирже, Invoice ID или тегу."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <Spin spinning={!!(q.trim()) && searching}>
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
            {list.map((vm) => (
              <OrderCard
                key={vm.id}
                vm={vm}
                platforms={platforms}
                invoices={invoicesMap[vm.id] || []}
                onAddInvoice={handleAddInvoice}
                onDeleteInvoice={handleDeleteInvoice}
                onDeleteOrder={handleDeleteOrder}
                onCloseDraft={handleCloseDraft}
                onApplyAllChanges={handleApplyAllChanges}
                isPinned={pinnedIds.has(vm.id)}
                onTogglePin={handleTogglePin}
                onAddTopup={handleAddTopup}
                onAddRefund={handleAddRefund}
                />
              ))}

              {/* Сентинел для бесконечной прокрутки (только когда нет поиска) */}
              {(((q || "").trim() && searchHasMore) || (!(q || "").trim() && hasMore)) && (
              <div ref={loaderRef} style={{ height: 1 }} />
              )}

              {!loading && list.length === 0 && <Card className="wo-empty">Нет ордеров</Card>}
            </Space>
          </Spin>
        </section>
      </div>
    </Spin>
  );
}
